import { useEffect, useMemo, useState } from 'react';
import { useEmergencyStore } from '@/stores/emergency-store';
import { supabase } from '@/services/supabase';
import { getCidDescription } from '@/services/arc';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ArrowLeft,
  AlertTriangle,
  Bell,
  Building2,
  Cctv,
  CheckCircle2,
  Clock,
  Copy,
  History,
  Lock,
  LockOpen,
  MapPin,
  Navigation,
  Phone,
  Radio,
  ShieldCheck,
  Users,
  Zap,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type Contact = {
  id: string;
  name: string;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  relation: string | null;
  priority: number | null;
  can_dispatch: boolean | null;
  can_verify: boolean | null;
};

type Panel = {
  id: string;
  name: string;
  model: string | null;
  serial_number: string | null;
  status: 'ONLINE' | 'OFFLINE' | 'TROUBLE' | string;
  is_armed: boolean | null;
  arm_mode: string | null;
  last_heartbeat_at: string | null;
  last_event_at: string | null;
};

type RecentEvent = {
  id: string;
  cid_code: string;
  cid_qualifier: 'E' | 'R';
  zone_no: number | null;
  severity: 'INFO' | 'WARNING' | 'ALARM' | 'CRITICAL';
  occurred_at: string;
  received_at: string;
  resolved_at: string | null;
  ticket_id: string | null;
  description_es: string | null;
};

const SEV_DOT: Record<RecentEvent['severity'], string> = {
  INFO: 'bg-blue-500',
  WARNING: 'bg-amber-500',
  ALARM: 'bg-red-600',
  CRITICAL: 'bg-red-700',
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatSeniority(createdAt: string): string {
  const months = (new Date().getFullYear() - new Date(createdAt).getFullYear()) * 12
    + (new Date().getMonth() - new Date(createdAt).getMonth());
  if (months < 1) return 'nuevo';
  if (months < 12) return `${months} mes${months !== 1 ? 'es' : ''}`;
  const y = Math.floor(months / 12), m = months % 12;
  return m > 0 ? `${y}a ${m}m` : `${y} año${y !== 1 ? 's' : ''}`;
}

// Extract a digit-only tel: target.
function telHref(raw: string | null | undefined) {
  if (!raw) return null;
  const cleaned = raw.replace(/[^\d+]/g, '');
  return cleaned.length >= 7 ? `tel:${cleaned}` : null;
}

function gmapsHref(lat: number | null, lng: number | null, address: string | null) {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  return null;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function SiteDetail() {
  const { selectedSite, selectSite, arcEvents, selectArcEvent } = useEmergencyStore();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [panels, setPanels] = useState<Panel[]>([]);
  const [history, setHistory] = useState<RecentEvent[]>([]);
  const [counts, setCounts] = useState<{ last24h: number; last7d: number; cameras: number } | null>(null);
  const [loading, setLoading] = useState(false);

  // Sites with one or more open tickets are "in dispatch" — we tap that flag
  // already on the marker itself so we don't need an extra fetch.
  const openEventsForSite = useMemo(
    () => arcEvents.filter((e) => e.site_id === selectedSite?.id),
    [arcEvents, selectedSite?.id],
  );

  // "Open in time" heuristic: an alarm site is considered OPEN (apertura) when
  // at least one of its panels is currently disarmed AND has reported a
  // heartbeat in the last 30 minutes — meaning someone is on premises with the
  // system unarmed. CLOSED (cierre) means every panel is armed. UNKNOWN means
  // no panel has been seen recently. Computed up front (before the early
  // return below) so the hooks order stays stable across renders.
  const armedPanels = useMemo(() => panels.filter((p) => p.is_armed).length, [panels]);
  const apertura: 'open' | 'closed' | 'unknown' = useMemo(() => {
    if (panels.length === 0) return 'unknown';
    const cutoff = Date.now() - 30 * 60 * 1000;
    const anyRecent = panels.some(
      (p) => p.last_heartbeat_at && new Date(p.last_heartbeat_at).getTime() >= cutoff,
    );
    if (!anyRecent) return 'unknown';
    return armedPanels < panels.length ? 'open' : 'closed';
  }, [panels, armedPanels]);

  useEffect(() => {
    if (!selectedSite) return;
    const siteId = selectedSite.id;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Parallel fetches; each is non-fatal on failure.
        const [contactsRes, panelsRes, historyRes, eventCountsRes, camerasRes] = await Promise.all([
          supabase
            .from('arc_contacts')
            .select('id, name, phone, mobile, email, relation, priority, can_dispatch, can_verify')
            .eq('site_id', siteId)
            .order('priority', { ascending: true })
            .limit(20),
          supabase
            .from('alarm_panels')
            .select('id, name, model, serial_number, status, is_armed, arm_mode, last_heartbeat_at, last_event_at')
            .eq('site_id', siteId)
            .order('name', { ascending: true }),
          supabase
            .from('arc_events')
            // NOTE: no join to cid_codes — that FK was intentionally dropped in
            // migration 20260826000000 (AX PRO panels send proprietary CID codes
            // not present in the reference table). Description comes from the
            // local getCidDescription() dictionary instead.
            .select('id, cid_code, cid_qualifier, zone_no, severity, occurred_at, received_at, resolved_at, ticket_id')
            .eq('site_id', siteId)
            .order('occurred_at', { ascending: false })
            .limit(15),
          supabase
            .from('arc_events')
            .select('received_at')
            .eq('site_id', siteId)
            .gte('received_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
            .limit(500),
          supabase
            .from('cameras')
            .select('id', { count: 'exact', head: true })
            .eq('site_id', siteId),
        ]);

        if (cancelled) return;

        if (contactsRes.data) setContacts(contactsRes.data as Contact[]);
        if (panelsRes.data) setPanels(panelsRes.data as Panel[]);

        if (historyRes.data) {
          type RawHistory = Omit<RecentEvent, 'description_es'>;
          const mapped = (historyRes.data as RawHistory[]).map((h) => {
            return {
              id: h.id,
              cid_code: h.cid_code,
              cid_qualifier: h.cid_qualifier,
              zone_no: h.zone_no,
              severity: h.severity,
              occurred_at: h.occurred_at ?? h.received_at,
              received_at: h.received_at,
              resolved_at: h.resolved_at,
              ticket_id: h.ticket_id,
              description_es: getCidDescription(h.cid_code),
            };
          });
          setHistory(mapped);
        }

        const cutoff24 = Date.now() - 24 * 60 * 60 * 1000;
        const last24h = (eventCountsRes.data ?? []).filter(
          (e) => new Date(e.received_at as string).getTime() >= cutoff24,
        ).length;
        const last7d = (eventCountsRes.data ?? []).length;
        setCounts({ last24h, last7d, cameras: camerasRes.count ?? 0 });
      } catch (err) {
        console.error('[SiteDetail] fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedSite]);

  if (!selectedSite) return null;
  const site = selectedSite;

  // ── Derived display values ────────────────────────────────────────────────
  const onlinePanels = panels.filter((p) => p.status === 'ONLINE').length;
  const aperturaLabel = apertura === 'open' ? 'Cuenta abierta' : apertura === 'closed' ? 'Cuenta cerrada' : 'Sin reporte';
  const aperturaTone =
    apertura === 'open'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
      : apertura === 'closed'
        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400';
  const aperturaIcon = apertura === 'open' ? LockOpen : apertura === 'closed' ? Lock : ShieldCheck;
  const AperturaIcon = aperturaIcon;

  const titleMain = site.account_name ? `${site.account_name} \u00b7 ${site.name}` : site.name;
  const phoneHref = telHref(site.account_phone);
  const mapsLink = gmapsHref(site.latitude, site.longitude, site.address);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-950">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-800 flex items-start gap-3">
        <button
          onClick={() => selectSite(null)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 mt-0.5"
          aria-label="Volver"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {site.account_number && (
              <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-300">
                #{site.account_number}
              </span>
            )}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${aperturaTone}`}>
              <AperturaIcon className="w-3 h-3" />
              {aperturaLabel}
            </span>
            {site.has_dispatched_ticket && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-300 inline-flex items-center gap-1">
                <Navigation className="w-3 h-3" />
                Patrulla en camino
              </span>
            )}
            {openEventsForSite.length > 0 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 inline-flex items-center gap-1">
                <Bell className="w-3 h-3" />
                {openEventsForSite.length} evento{openEventsForSite.length === 1 ? '' : 's'} abiertos
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-1">
            {titleMain}
          </h3>
          {(site.account_type || site.account_tier || site.account_created_at) && (
            <p className="text-[11px] text-gray-500 dark:text-slate-400 capitalize mt-0.5 flex items-center gap-1.5 flex-wrap">
              {site.account_tier && (
                <span className={`font-bold px-1.5 py-0.5 rounded-full border text-[10px] ${
                  site.account_tier === 'A' ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600' :
                  site.account_tier === 'B' ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-600' :
                  'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600'
                }`}>
                  Tier {site.account_tier}
                </span>
              )}
              {site.account_type && <span>{site.account_type}{site.account_city ? ` · ${site.account_city}` : ''}</span>}
              {site.account_created_at && <span>· {formatSeniority(site.account_created_at)}</span>}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        {/* Quick contact strip */}
        <div className="grid grid-cols-2 gap-2">
          {phoneHref ? (
            <a
              href={phoneHref}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-900 text-white hover:bg-blue-800 transition-colors text-xs font-semibold"
              title="Llamar vía Webex / softphone"
            >
              <Phone className="w-4 h-4" />
              <span className="truncate">{site.account_phone}</span>
            </a>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-400 text-xs">
              <Phone className="w-4 h-4" />
              <span>Sin teléfono</span>
            </div>
          )}
          {mapsLink ? (
            <a
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-200 text-xs font-semibold"
              title="Abrir en Google Maps"
            >
              <MapPin className="w-4 h-4" />
              <span className="truncate">Ver en Maps</span>
            </a>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-400 text-xs">
              <MapPin className="w-4 h-4" />
              <span>Sin ubicación</span>
            </div>
          )}
        </div>

        {site.address && (
          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-slate-400">
            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{site.address}</span>
            <button
              onClick={() => navigator.clipboard?.writeText(site.address ?? '')}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"
              title="Copiar dirección"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-4 gap-2">
          <KpiTile
            icon={<Radio className="w-3.5 h-3.5" />}
            label="Paneles"
            value={`${onlinePanels}/${panels.length}`}
            tone={panels.length === 0 ? 'muted' : onlinePanels === panels.length ? 'good' : 'warn'}
            sub="online"
          />
          <KpiTile
            icon={<Lock className="w-3.5 h-3.5" />}
            label="Armados"
            value={`${armedPanels}/${panels.length}`}
            tone={panels.length === 0 ? 'muted' : armedPanels > 0 ? 'good' : 'warn'}
          />
          <KpiTile
            icon={<Zap className="w-3.5 h-3.5" />}
            label="24 h"
            value={String(counts?.last24h ?? '—')}
            tone={(counts?.last24h ?? 0) > 5 ? 'warn' : 'muted'}
            sub="eventos"
          />
          <KpiTile
            icon={<Cctv className="w-3.5 h-3.5" />}
            label="Cámaras"
            value={String(counts?.cameras ?? '—')}
            tone={(counts?.cameras ?? 0) > 0 ? 'good' : 'muted'}
          />
        </div>

        {/* Open events */}
        {openEventsForSite.length > 0 && (
          <Section title={`Eventos abiertos (${openEventsForSite.length})`} icon={<AlertTriangle className="w-3.5 h-3.5 text-red-500" />}>
            <ul className="space-y-1">
              {openEventsForSite.slice(0, 6).map((ev) => (
                <li key={ev.id}>
                  <button
                    onClick={() => selectArcEvent(ev)}
                    className="w-full text-left px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-900/40"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-red-800 dark:text-red-200 truncate">
                        {ev.description_es ?? `CID ${ev.cid_code}`}
                      </span>
                      <span className="text-[10px] text-red-600 dark:text-red-300 whitespace-nowrap">
                        {formatDistanceToNow(new Date(ev.received_at), { addSuffix: true, locale: es })}
                      </span>
                    </div>
                    {ev.zone_no != null && (
                      <p className="text-[10px] text-red-600 dark:text-red-300 mt-0.5">Zona {ev.zone_no}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Contacts (priority order) */}
        <Section
          title={`Contactos (${contacts.length})`}
          icon={<Users className="w-3.5 h-3.5 text-blue-700" />}
        >
          {contacts.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-slate-400 italic">Sin contactos registrados.</p>
          ) : (
            <ul className="space-y-1.5">
              {contacts.map((c) => {
                const primaryPhone = c.mobile ?? c.phone;
                const href = telHref(primaryPhone);
                return (
                  <li key={c.id} className="px-3 py-2 rounded-md bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono px-1.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
                            #{c.priority ?? '?'}
                          </span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                            {c.name}
                          </span>
                        </div>
                        {c.relation && (
                          <p className="text-[11px] text-gray-500 dark:text-slate-400 capitalize">{c.relation}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {c.can_dispatch && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                              Despacha
                            </span>
                          )}
                          {c.can_verify && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              Verifica
                            </span>
                          )}
                        </div>
                      </div>
                      {href ? (
                        <a
                          href={href}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-900 text-white text-[11px] font-semibold hover:bg-blue-800 whitespace-nowrap"
                        >
                          <Phone className="w-3 h-3" />
                          {primaryPhone}
                        </a>
                      ) : (
                        <span className="text-[10px] text-gray-400">Sin tel.</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Panels */}
        <Section
          title={`Paneles (${panels.length})`}
          icon={<Building2 className="w-3.5 h-3.5 text-blue-700" />}
        >
          {panels.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-slate-400 italic">Este sitio aún no tiene paneles enlazados.</p>
          ) : (
            <ul className="space-y-1.5">
              {panels.map((p) => {
                const isOnline = p.status === 'ONLINE';
                return (
                  <li key={p.id} className="px-3 py-2 rounded-md bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{p.name}</p>
                        <p className="text-[10px] text-gray-500 dark:text-slate-400">
                          {p.model ?? 'Modelo desconocido'}
                          {p.serial_number ? ` \u00b7 SN ${p.serial_number}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            isOnline
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400'
                          }`}
                        >
                          {p.status}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                            p.is_armed
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                          }`}
                        >
                          {p.is_armed ? <Lock className="w-2.5 h-2.5" /> : <LockOpen className="w-2.5 h-2.5" />}
                          {p.is_armed ? `Armado${p.arm_mode ? ` · ${p.arm_mode}` : ''}` : 'Desarmado'}
                        </span>
                      </div>
                    </div>
                    {p.last_heartbeat_at && (
                      <p className="text-[10px] text-gray-500 dark:text-slate-500 mt-1 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        Heartbeat {formatDistanceToNow(new Date(p.last_heartbeat_at), { addSuffix: true, locale: es })}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Recent events history */}
        <Section
          title={`Historial reciente${counts ? ` (${counts.last7d} en 7 días)` : ''}`}
          icon={<History className="w-3.5 h-3.5 text-gray-500" />}
        >
          {loading && history.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-slate-400 italic">Cargando…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-slate-400 italic">Sin eventos registrados.</p>
          ) : (
            <ul className="space-y-1">
              {history.map((h) => {
                const resolved = !!h.resolved_at;
                return (
                  <li
                    key={h.id}
                    className="px-3 py-1.5 rounded-md bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[h.severity]}`} />
                      <span className="text-xs text-gray-800 dark:text-slate-200 truncate flex-1">
                        {h.description_es ?? `CID ${h.cid_code}`}
                        {h.zone_no != null ? ` · Z${h.zone_no}` : ''}
                      </span>
                      {resolved ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Bell className="w-3 h-3 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500 dark:text-slate-500 ml-4">
                      {format(new Date(h.received_at), "d MMM yyyy HH:mm", { locale: es })}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Local presentational helpers
// -----------------------------------------------------------------------------

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 flex items-center gap-1.5 mb-2">
        {icon}
        {title}
      </h4>
      {children}
    </section>
  );
}

type Tone = 'good' | 'warn' | 'muted';
function KpiTile({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone: Tone;
}) {
  const toneClass =
    tone === 'good'
      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300'
      : tone === 'warn'
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-300'
        : 'bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-700 dark:text-slate-300';
  return (
    <div className={`px-2 py-2 rounded-md border ${toneClass}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[9px] opacity-70">{sub}</div>}
    </div>
  );
}
